import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Edit, Trash2, HelpCircle, CheckCircle, X } from 'lucide-react';
import { QuizQuestion } from '@/types/quiz';

interface QuestionListProps {
  questions: QuizQuestion[];
  onEdit: (question: QuizQuestion) => void;
  onDelete: (questionId: string) => void;
}

export const QuestionList = ({ questions, onEdit, onDelete }: QuestionListProps) => {
  const getQuestionTypeDisplay = (type: string) => {
    const types = {
      multiple_choice: { label: 'Multiple Choice', icon: '🔘' },
      true_false: { label: 'True/False', icon: '✓/✗' },
      fill_in_blank: { label: 'Fill in Blank', icon: '📝' }
    };
    return types[type as keyof typeof types] || { label: type, icon: '❓' };
  };

  const renderQuestionPreview = (question: QuizQuestion) => {
    if (question.question_type === 'true_false') {
      const correctAnswer = question.options?.find(opt => opt.is_correct);
      return (
        <div className="mt-3 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Correct Answer:</span>
            <Badge variant="outline">
              {correctAnswer?.option_text === 'true' ? 'True' : 'False'}
            </Badge>
          </div>
        </div>
      );
    }

    if (question.question_type === 'fill_in_blank') {
      const correctAnswer = question.options?.[0];
      return (
        <div className="mt-3 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Correct Answer:</span>
            <Badge variant="outline">{correctAnswer?.option_text}</Badge>
          </div>
        </div>
      );
    }

    // Multiple choice
    return (
      <div className="mt-3 space-y-2">
        {question.options?.slice(0, 4).map((option, index) => (
          <div key={option.id || index} className="flex items-center gap-2 text-sm">
            {option.is_correct ? (
              <CheckCircle className="h-4 w-4 text-green-600" />
            ) : (
              <div className="h-4 w-4 rounded-full border border-gray-300" />
            )}
            <span className={option.is_correct ? 'text-green-600 font-medium' : 'text-muted-foreground'}>
              {option.option_text}
            </span>
          </div>
        ))}
        {question.options && question.options.length > 4 && (
          <div className="text-xs text-muted-foreground">
            +{question.options.length - 4} more options
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {questions.map((question, index) => {
        const typeDisplay = getQuestionTypeDisplay(question.question_type);
        
        return (
          <Card key={question.id} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="flex items-center justify-center w-8 h-8 bg-primary text-primary-foreground rounded-full text-sm font-semibold">
                      {index + 1}
                    </div>
                    <Badge variant="secondary">
                      {typeDisplay.icon} {typeDisplay.label}
                    </Badge>
                    <Badge variant="outline">
                      {question.points_value} points
                    </Badge>
                  </div>
                  
                  <h4 className="text-lg font-medium mb-2">{question.question_text}</h4>
                  
                  {question.explanation && (
                    <div className="flex items-start gap-2 mb-3 p-3 bg-blue-50 rounded-lg">
                      <HelpCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-blue-800">{question.explanation}</p>
                    </div>
                  )}
                  
                  {renderQuestionPreview(question)}
                </div>
                
                <div className="flex items-center gap-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEdit(question)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onDelete(question.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};